<template>
  <form @submit.prevent="handleSubmit" class="px-14 flex flex-col items-center">
    <div class="w-full max-w-md">
      <TitlePrimary :title="$t('admin.form.titles.new')" />
    </div>

    <div v-if="loading" class="text-center p-4">{{ $t('admin.loading') }}</div>

    <template v-else>
      <div class="py-8 w-full max-w-md">

        <SelectPrimary
          :error="errors.tipoCuenta"
          :options="tipoCuentaOptions"
          v-model="form.tipoCuenta"
          name="tipoCuenta"
          :label="$t('admin.form.labels.accountType') + ':'"
          class="max-w-[350px]"
        />
        <InputPrimary
          v-model="form.nombre"
          :error="errors.nombre"
          class="max-w-[350px]"
          type="text"
          :label="$t('admin.form.labels.name') + ':'"
        />
        <InputPrimary
          v-model="form.apellidos"
          :error="errors.apellidos"
          class="max-w-[350px]"
          type="text"
          :label="$t('admin.form.labels.surname') + ':'"
        />
        <InputPrimary
          v-model="form.dni_nie"
          :error="errors.dni_nie"
          class="max-w-[350px]"
          type="text"
          :label="$t('admin.form.labels.identityDocument') + ' (opcional):'"
        />
        <InputPrimary
          :modelValue="form.telefono"
          @update:modelValue="handleTelefonoUpdate"
          :error="errors.telefono"
          class="max-w-[350px]"
          type="tel"
          :label="$t('admin.form.labels.mobile') + ' (opcional):'"
        />
        <InputPrimary
          v-model="form.email"
          :error="errors.email"
          class="max-w-[350px]"
          type="email"
          :label="$t('admin.form.labels.email') + ':'"
        />
        

          <div class="w-full max-w-[350px] py-4 flex items-center justify-center">
            <ButtonWhite
              :title="$t('admin.form.buttons.save')"
              bgColor="bg-[#25B0D7]"
              textColor="text-[#fff]"
              hoverBgColor="hover:bg-[#5F99AE]"
              class="w-full"
              type="submit"
            />
          </div>
      </div>

    </template>
  </form>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { toast } from 'vue3-toastify';

import ButtonWhite from '@/components/button/ButtonWhite.vue';
import InputPrimary from '@/components/inputs/input-primary.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import SelectPrimary from '@/components/select/SelectPrimary.vue';
import { hasAnyRole } from '@/services/userRoles';

const router = useRouter();
const { t } = useI18n();
const loading = ref(false);

const form = reactive({
  nombre: '',
  apellidos: '',
  dni_nie: '',
  telefono: '',
  email: '',
  tipoCuenta: '',
  has_notifications: true,
  maintenance: false,
});

const errors = reactive({
  nombre: '',
  apellidos: '',
  dni_nie: '',
  email: '',
  tipoCuenta: '',
  telefono: '',
});

const telefonoHasInvalidCharacters = ref(false);

const normalizePhone = (value) => String(value || '').replace(/[^\d]/g, '');

const handleTelefonoUpdate = (value) => {
  telefonoHasInvalidCharacters.value = /[^\d]/.test(String(value || ''));
  form.telefono = normalizePhone(value);

  if (!telefonoHasInvalidCharacters.value) {
    errors.telefono = '';
  }
};

// Back Office da de alta comerciales y otros Back Office; los
// superadministradores, solo el responsable (ADSREC-0007). Los nombres son los
// mismos que en el listado de Usuarios ADS.
const tipoCuentaOptions = ['comercial', 'admin', ...(hasAnyRole(['superadmin']) ? ['superadmin'] : [])]
  .map((value) => ({ value, label: t(`common.requestTypes.${value}`) }));

const validateForm = () => {
  let isValid = true;
  errors.nombre = '';
  errors.apellidos = '';
  errors.dni_nie = '';
  errors.email = '';
  errors.tipoCuenta = '';
  errors.telefono = '';

  if (!form.nombre.trim()) {
    errors.nombre = 'Este campo es obligatorio';
    isValid = false;
  }
  if (!form.apellidos.trim()) {
    errors.apellidos = 'Este campo es obligatorio';
    isValid = false;
  }
  if (!form.email.trim()) {
    errors.email = 'Este campo es obligatorio';
    isValid = false;
  }
  if (!form.tipoCuenta) {
    errors.tipoCuenta = 'Este campo es obligatorio';
    isValid = false;
  }
  if (telefonoHasInvalidCharacters.value || (form.telefono && !/^\d{8,15}$/.test(form.telefono))) {
    errors.telefono = 'El teléfono debe tener entre 8 y 15 dígitos numéricos';
    isValid = false;
  }

  if (!isValid) {
    toast.error('Por favor, complete todos los campos obligatorios.', { position: 'top-right' });
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  loading.value = true;
  try {
    // Sin contraseña: la API envía el correo para que la persona defina la
    // suya. Si viaja una, el alta exige confirmarla, como en el de clientes.
    const payload = {
      name: form.nombre,
      surname: form.apellidos,
      email: form.email,
      role: form.tipoCuenta,
      has_notifications: form.has_notifications,
      maintenance: form.maintenance,
    };

    if (form.telefono.trim()) {
      payload.mobile = normalizePhone(form.telefono);
    }
    if (form.dni_nie.trim()) {
      payload.dni_nie = form.dni_nie.trim();
    }

    await axios.post(
      `${import.meta.env.VITE_API_URL}/admin/create`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      }
    );

    toast.success('Usuario creado exitosamente', {
      position: 'top-right',
      autoClose: 2000,
    });
    setTimeout(() => {
      router.go(-1);
    }, 1500);
  } catch (err) {
    const serverErrors = err?.response?.data?.errors;
    if (serverErrors && typeof serverErrors === 'object') {
      const map = { name: 'nombre', surname: 'apellidos', dni_nie: 'dni_nie', email: 'email', role: 'tipoCuenta' };
      Object.keys(serverErrors).forEach((key) => {
        const local = map[key] || key;
        if (errors[local] !== undefined) {
          const msg = Array.isArray(serverErrors[key]) ? serverErrors[key][0] : serverErrors[key];
          errors[local] = msg;
        }
      });
      toast.error('Por favor, corrija los errores del formulario.', { position: 'top-right' });
    } else {
      toast.error('Error al crear el usuario.', { position: 'top-right' });
    }
  } finally {
    loading.value = false;
  }
};
</script>
